"use client";
import { useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import MMVCarLoan from "../MMVCarLoan";
import MMVHeroSection from "../MMVHeroSection";
import MMVPriceBreakup from "../MMVPriceBreakup";
import { Modal } from "@components/atoms/Modal";
import styles from "./MMVTopFold.module.scss";
import { getDaysUntilDateString } from "@utils/formatDate";
import Button from "@components/atoms/Button";
import IntentModal from "@components/molecules/IntentModal";
import RequestCallBackModal from "@components/molecules/RequestCallBackModal";
import StickyCTA from "@components/molecules/StickyCTA";
import { addCaptureIntentIfNeeded, getLoginUrl } from "@utils/getLoginUrl";
import { EventType, trackEventWithCommonProps } from "@utils/eventTracker";
import { postLead } from "@services/leadCaptureService";
import { addUTMParamsToLeadData } from "@utils/utmParams";
import { postRequestCallback } from "@services/requestCallbackService";
import { getPlatform, inApp } from "@utils/platformDetection.client";
import GlobalIntentModal from "@components/molecules/GlobalIntentModal";
import ModalManager from "@components/molecules/ModalManager";
import Image from "next/image";
import assetLoader from "@utils/internalImageLoder";
import { AwardTrustMarker } from "@interfaces/trust-markers.types";
import { LoginSdkConfig } from "@enums/LoginSdkConfig";
import { handleLoginFlow } from "@utils/loginFlowHandler";
import { formatPrice } from "@utils/numberFormatter";
import { getLeadData } from "@utils/leadDataStorage";
import { getClientCookie } from "@lib/cookies.client";
import { createOMSQuote } from "@services/oms-service";
import { getFeatureValue, loadGrowthbookFeatures } from "@utils/growthBook";
import { useGlobalLoader } from "@hooks/useGlobalLoader";

type Color = {
  id: string;
  hexCodes: string[];
  name: string;
  brandColor: string;
  Inventory_details: any;
  Adsp_details: any;
};

function MMVTopFold({
  variantDetails,
  carFinanceData,
  carFinanceBankDetails,
  isLoggedIn,
  slug,
  cityName,
  buyingIntent,
  isSellable,
  pageType,
  carAvailability,
  cityId,
  userDetails,
  savingBalance,
  environmentCompensation,
  trustMarkerBadge,
  awardTrustMarkers,
  showAwards,
  userId,
}: any & {
  trustMarkerBadge?: string | null;
  awardTrustMarkers?: AwardTrustMarker[];
  showAwards?: boolean;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const ACKODRIVE_OMS_QUOTE = "ackodrive20_oms";
  const { showLoader, hideLoader } = useGlobalLoader();

  const {
    brand_name: brandName,
    model_name: modelName,
    variant_name: variantName,
    availableColors: colors,
    basic_features: basicFeatures,
    price,
  } = variantDetails || {};

  const {
    variant_color_id: baseColorId,
    inventory_alert_dict: inventoryAlertData,
  } = price || {};

  const availableYears = variantDetails?.available_year_list;
  const selectedColor = colors?.find((c: Color) => c.id === baseColorId);

  const getFeatureValues = (key: string) =>
    basicFeatures?.find((feature: any) => feature.key === key)?.value;

  const seatingCapacity = getFeatureValues("seat");
  const transmission = getFeatureValues("transmission");
  const fuelType = getFeatureValues("fuel_type");

  const mmvName = `${brandName} ${modelName} ${variantName}`;
  const isInventoryAvaialable =
    Number(selectedColor?.Inventory_details?.market_price) > 0;
  const selectedDeliveryData = price;
  const { Inventory_details: inventoryDetails, Adsp_details: adspDetails } =
    selectedColor || {};
  const deliveryOptions = { inventory: inventoryDetails, adsp: adspDetails };

  const modelYearParam = searchParams?.get("model_year");
  const initialModelYear = modelYearParam
    ? modelYearParam : availableYears?.[0];

  const [selectedDelivery, setSelectedDelivery] =
    useState(selectedDeliveryData);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isIntentModalOpen, setIsIntentModalOpen] = useState(false);
  const [isRequestCallBackModalOpen, setIsRequestCallBackModalOpen] =
    useState(false);
  // Store delivery type as string for modal selection
  const [tempSelectedDeliveryType, setTempSelectedDeliveryType] =
    useState<string>(selectedDelivery?.deal_type?.toLowerCase() || "adsp");
  const [isHeroCtaVisible, setIsHeroCtaVisible] = useState(true);
  const [selectedModelYear, setSelectedModelYear] =
    useState<string>(initialModelYear);

  // Mobile app intent modal state
  const [isMobileAppIntentModalOpen, setIsMobileAppIntentModalOpen] =
    useState(false);

  const isMobileApp = inApp();

  const carDetails = {
    name: mmvName,
    color: {
      name: selectedColor?.brand_color || "Unknown Colour",
      hex: selectedColor?.base_colors_hexcodes || ["#000000"],
      type: selectedColor?.color_type || "",
    },
  };

  // Mobile app intent modal helpers
  const shouldShowMobileAppIntent = () => {
    if (typeof window === "undefined") return false;
    if (!isMobileApp) return false;
    if (!isLoggedIn) return false;
    const sessionIntentCaptured = sessionStorage.getItem("buying_intent");
    if (sessionIntentCaptured) return false;

    return true;
  };

  const leadCaptureHandler: (isCallback?: boolean) => Promise<void> = async (
    isCallback = false
  ) => {
    let leadData: any = {
      city: cityName,
      make: brandName || "",
      model: modelName || "",
      variant: variantName || "",
      color: selectedColor?.brand_color || "",
    };

    // If this is a callback request, set high intent automatically
    if (isCallback) {
      leadData.buying_period = "Within a month";
    }

    // const storedSource = sessionStorage.getItem("referrerSource");
    const utmTerm =
      new URL(window.location.href).searchParams.get("utm_term") || "";
    leadData.dump_data = {
      mx_Platform: getPlatform(),
      mx_Page_Type_Web: pageType,
      mx_Login_Type: isCallback ? "Request callback" : "Get quote",
      // SourceReferrerURL: storedSource,
      mx_URL: window.location.href || process.env.NEXT_PUBLIC_FRONTEND_HOST,
      mx_UTM_Term: utmTerm,
      mx_color: leadData?.color || "",
    };

    // Get existing lead data with fallback to localStorage
    const existingLeadData = await getLeadData();
    const mergedLeadData = { ...existingLeadData, ...leadData };
    leadData = mergedLeadData;
    const buyingPeriod = isCallback
      ? "Within a month"
      : leadData?.buying_period || "";
    try {
      const leadResponse = await postLead(addUTMParamsToLeadData(leadData));
      if (!leadResponse.success) {
        console.error("Lead capture failed.", leadResponse);
        await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
          pageType: pageType,
          additionalProps: {
            exception_type: "API issue",
            exception_name: "failed lead creation",
            car_availability: carAvailability,
            vehicle_make: brandName,
            vehicle_model: modelName,
            vehicle_variant: variantName,
            vehicle_color: selectedColor?.brand_color,
            city_name: cityName,
            login_type: "Get quote",
            intent: buyingPeriod,
          },
        });
      } else {
        const leadCreated = !!leadResponse?.data?.is_created;
        await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
          pageType: pageType,
          additionalProps: {
            action_type: leadCreated ? "lead_created" : "lead_updated",
            car_availability: carAvailability,
            vehicle_make: brandName,
            vehicle_model: modelName,
            vehicle_variant: variantName,
            city_name: cityName,
            vehicle_color: selectedColor?.brand_color,
            login_type: "Get quote",
            intent: buyingPeriod,
          },
        });
      }
    } catch (error) {
      console.error("Error in postLead API call:", error);
      await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
        pageType: pageType,
        additionalProps: {
          exception_type: "API issue",
          exception_name: "failed lead creation",
          car_availability: carAvailability,
          vehicle_make: brandName,
          vehicle_model: modelName,
          vehicle_variant: variantName,
          vehicle_color: selectedColor?.brand_color,
          city_name: cityName,
          login_type: "Get quote",
          intent: buyingPeriod,
        },
      });
    }
  };

  const handleModelYearChange = (year: string) => {
    if (year === selectedModelYear) return;

    showLoader(`Loading ${year} model`);
    setSelectedModelYear(year);

    const currentParams = new URLSearchParams(searchParams?.toString() || "");
    currentParams.set("model_year", year);

    // Handle delivery type based on selected year
    if (availableYears && availableYears.length >= 2) {
      const minYear = Math.min(...availableYears.map((y: string) => parseInt(y)));
      const maxYear = Math.max(...availableYears.map((y: string) => parseInt(y)));
      const selectedYearNum = parseInt(year);
      
      // If switching to min year, always set to inventory (express)
      if (selectedYearNum === minYear) {
        currentParams.set("deliveryType", "inventory");
      } 
      // If switching to max year, check if express delivery is available for selected color
      else if (selectedYearNum === maxYear) {
        const isExpressDeliveryAvailable = selectedColor?.is_express_delivery === true;
        if (isExpressDeliveryAvailable) {
          currentParams.set("deliveryType", "inventory");
        } else {
          currentParams.set("deliveryType", "adsp");
        }
      }
    }

    const queryString = currentParams.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

    router.push(newUrl);
  };

  const handleDeliveryTypeChange = (deliveryType: "inventory" | "adsp") => {
    const currentDeliveryType = selectedDelivery?.deal_type?.toLowerCase();
    if (currentDeliveryType === deliveryType) {
      setIsModalOpen(false);
      return;
    }

    setIsModalOpen(false);
    
    if (availableYears && availableYears.length >= 2) {
      const minYear = Math.min(...availableYears.map((y: string) => parseInt(y)));
      const maxYear = Math.max(...availableYears.map((y: string) => parseInt(y)));
      const currentYear = selectedModelYear ? parseInt(selectedModelYear) : null;
      
      // If on min year and selecting adsp (standard), switch to max year with adsp
      if (currentYear === minYear && deliveryType === "adsp") {
        showLoader(`Loading standard delivery pricing`);
        setSelectedModelYear(String(maxYear));
        const currentParams = new URLSearchParams(searchParams?.toString() || "");
        currentParams.set("model_year", String(maxYear));
        currentParams.set("deliveryType", "adsp");
        const queryString = currentParams.toString();
        const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
        router.push(newUrl);
        return;
      }
      
      // If on max year and selecting inventory (express), check if express delivery is available
      if (currentYear === maxYear && deliveryType === "inventory") {
        const isExpressDeliveryAvailable = selectedColor?.is_express_delivery === true;
        
        if (!isExpressDeliveryAvailable) {
          showLoader(`Loading ${minYear} model with express delivery`);
          setSelectedModelYear(String(minYear));
          const currentParams = new URLSearchParams(searchParams?.toString() || "");
          currentParams.set("model_year", String(minYear));
          currentParams.set("deliveryType", "inventory");
          const queryString = currentParams.toString();
          const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
          router.push(newUrl);
          return;
        }
        showLoader(`Loading express delivery pricing`);
        const currentParams = new URLSearchParams(searchParams?.toString() || "");
        currentParams.set("deliveryType", "inventory");
        const queryString = currentParams.toString();
        const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
        router.push(newUrl);
        return;
      }
    }

    showLoader(`Loading ${deliveryType === "inventory" ? "Express" : "Standard"} delivery`);
    
    const currentParams = new URLSearchParams(searchParams?.toString() || "");
    currentParams.set("deliveryType", deliveryType);

    const queryString = currentParams.toString();
    const newUrl = queryString ? `${pathname}?${queryString}` : pathname;

    router.push(newUrl);
  };

  const handleSelectDelivery = () => {
    if (!tempSelectedDeliveryType) return;
    
    if (tempSelectedDeliveryType === "inventory" || tempSelectedDeliveryType === "adsp") {
      handleDeliveryTypeChange(tempSelectedDeliveryType as "inventory" | "adsp");
    }
  };

  // Intent Modal handlers
  const handleRequestCallback = async (intent?: string) => {
    setIsIntentModalOpen(false);
    setIsRequestCallBackModalOpen(true);
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: pageType,
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_help",
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_color: selectedColor?.brand_color,
        vehicle_variant: variantName,
        customer_name: userDetails.name,
        car_availability: carAvailability,
        email: userDetails.email,
        phone: userDetails.phone,
      },
    });
  };

  const handleCreateOMSQuote = async () => {
    try {
      const colorSlug = selectedColor?.brand_color
        ?.toLowerCase()
        .replace(/\s+/g, "-");
      const payload = {
        name: brandName,
        delivery_type:
          selectedDelivery?.deal_type?.toLowerCase() === "adsp"
            ? "Standard"
            : "Express",
        city: cityName,
        items: [
          {
            sku_id: slug,
            sku_type: "CAR",
            color_name: colorSlug || "",
          },
        ],
        customer_name: userDetails?.name,
        email: userDetails?.email,
        phone_number: userDetails?.phone,
        journey: "new_car",
      };
      const authCookie = getClientCookie("ackodrive_tokens");
      if (!authCookie) {
        return;
      }

      const token = JSON.parse(authCookie).access;

      const quoteResponse = await createOMSQuote(payload, token);
      if (quoteResponse?.data?.quote_ekey) {
        router.push(`/quote/v2/${quoteResponse?.data?.quote_ekey}`);
      }
    } catch (error) {
      console.error("Error creating quote:", error);
      return;
    }
  };

  const handleRouting = async (intent?: any) => {
    try {
      await loadGrowthbookFeatures(userId);
      const showNewQuoteFlow = getFeatureValue({
        flagName: ACKODRIVE_OMS_QUOTE,
      });
      if (showNewQuoteFlow) {
        handleCreateOMSQuote();
      } else {
        // If buying_intent is "false", directly navigate to quote page
        const currentParams = new URLSearchParams(
          searchParams?.toString() || ""
        );
        const currentDeliveryType = selectedDelivery?.deal_type;

        if (currentDeliveryType) {
          currentParams.set(
            "deal_type",
            (currentDeliveryType || "").toLowerCase()
          );
          currentParams.set("color_id", selectedColor?.id);
        }
        if(selectedModelYear && availableYears && availableYears.length > 1){
          currentParams.set("model_year", selectedModelYear);
        }
        if (intent) {
          currentParams.set("intent", intent);
        }
        const quoteUrl = `/quote/${slug}?${currentParams.toString()}`;
        router.push(quoteUrl);
      }
    } catch (error) {
      console.error("Error Loading GrowthBook:", error);
      return;
    }
  };

  const handleGetQuote = async (intent?: string) => {
    // Check if buying_intent is not "false" before opening intent modal
    if (sessionStorage.getItem("buying_intent") !== "false") {
      setIsIntentModalOpen(true);
    } else {
      leadCaptureHandler();
      
      await trackEventWithCommonProps("view", EventType.TRACK_EVENT, {
        pageType: "Intent",
        additionalProps: {
          view_type: "view_page",
          view_name: "view_new_car_intent",
          city_name: cityName,
          vehicle_make: brandName,
          vehicle_model: modelName,
          vehicle_color: selectedColor?.brand_color,
          car_availability: carAvailability,
          vehicle_variant: variantName,
          customer_name: userDetails.name,
          email: userDetails.email,
          phone: userDetails.phone,
          intent: intent,
        },
      });

      handleRouting(intent);
    }
  };

  const handleGetQuoteFromIntent = async (intent?: string) => {
   
    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: "Intent",
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_get_detailed_quote",
        city_name: cityName,
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_color: selectedColor?.brand_color,
        vehicle_variant: variantName,
        customer_name: userDetails.name,
        email: userDetails.email,
        phone: userDetails.phone,
        intent: intent,
        delivery_type: selectedDelivery?.deal_type,
        car_availability: carAvailability,
      },
    });
    handleRouting(intent);
    setIsIntentModalOpen(false);
  };

  useEffect(() => {
    trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
      pageType: pageType,
      additionalProps: {
        view_type: "view_page",
        view_name: "view_product_details_page",
        vehicle_make: brandName,
        vehicle_model: modelName,
        vehicle_variant: variantName,
        city_name: cityName,
        car_availability: carAvailability,
      },
    });
  }, []);

  useEffect(() => {
    setSelectedDelivery(selectedDeliveryData);
    setTempSelectedDeliveryType(selectedDeliveryData?.deal_type?.toLowerCase() || "adsp");
  }, [pathname, searchParams, selectedDeliveryData]);

  useEffect(() => {
    const deliveryTypeParam = searchParams?.get("deliveryType");
    const currentDeliveryType = selectedDeliveryData?.deal_type?.toLowerCase();
    if (deliveryTypeParam && deliveryTypeParam.toLowerCase() === currentDeliveryType) {
      hideLoader();
    }
  }, [selectedDeliveryData, searchParams, hideLoader]);

  useEffect(() => {
    const urlModelYear = searchParams?.get("model_year");
    if (urlModelYear === selectedModelYear) {
      hideLoader();
    }
  }, [selectedModelYear, searchParams, hideLoader]);

  useEffect(()=>{
    if(availableYears && availableYears.length > 1){
      setSelectedModelYear(modelYearParam ?? availableYears?.[0]);
    }
  }, [modelYearParam, availableYears]);

  useEffect(() => {
    if (availableYears && availableYears.length >= 2) {
      const minYear = Math.min(...availableYears.map((y: string) => parseInt(y)));
      const maxYear = Math.max(...availableYears.map((y: string) => parseInt(y)));
      const currentYearParam = searchParams?.get("model_year");
      const deliveryTypeParam = searchParams?.get("deliveryType")?.toLowerCase();
      
      if (!currentYearParam) return;
      const currentYear = parseInt(currentYearParam);
      if (currentYear === minYear && deliveryTypeParam === "adsp") {
        const currentParams = new URLSearchParams(searchParams?.toString() || "");
        currentParams.set("deliveryType", "inventory");
        const queryString = currentParams.toString();
        const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
        router.push(newUrl);
        return;
      }
      
      if (currentYear === maxYear) {
        const isExpressDeliveryAvailable = selectedColor?.is_express_delivery === true;
        if (!isExpressDeliveryAvailable && deliveryTypeParam && deliveryTypeParam !== "adsp") {
          const currentParams = new URLSearchParams(searchParams?.toString() || "");
          currentParams.set("deliveryType", "adsp");
          const queryString = currentParams.toString();
          const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
          router.push(newUrl);
          return;
        }
      }
    }
  }, [searchParams, availableYears, pathname, router, selectedColor]);

  useEffect(() => {
    if (isModalOpen) {
      setTempSelectedDeliveryType(selectedDelivery?.deal_type?.toLowerCase() || "adsp");
    }
  }, [isModalOpen, selectedDelivery]);

  // Mobile app intent modal useEffect hooks
  useEffect(() => {
    if (shouldShowMobileAppIntent()) {
      setIsMobileAppIntentModalOpen(true);
    }
  }, []);

  return (
    <>
      <MMVHeroSection
        mmvName={mmvName}
        brandName={brandName}
        modelName={modelName}
        variantName={variantName}
        colors={colors}
        selectedColor={selectedColor}
        seatingCapacity={seatingCapacity}
        fuelType={fuelType}
        transmission={transmission}
        price={selectedDelivery}
        isLoggedIn={isLoggedIn}
        isInventoryAvailable={isInventoryAvaialable}
        deliveryOptions={{}}
        selectedDelivery={selectedDelivery}
        setIsModalOpen={setIsModalOpen}
        setIsIntentModalOpen={setIsIntentModalOpen}
        handleGetQuote={handleGetQuote}
        cityName={cityName}
        inventoryAlertData={inventoryAlertData}
        onCtaVisibilityChange={setIsHeroCtaVisible}
        isSellable={isSellable}
        carAvailability={carAvailability}
        pageType={pageType}
        trustMarkerBadge={trustMarkerBadge}
        awardTrustMarkers={awardTrustMarkers}
        showAwards={showAwards}
        selectedYear={selectedModelYear}
        onYearChange={handleModelYearChange}
        availableYears={availableYears}
        priceData={variantDetails?.price}
      />
      <MMVPriceBreakup
        price={price}
        isLoggedIn={isLoggedIn}
        selectedDelivery={selectedDelivery}
        setIsModalOpen={setIsModalOpen}
        setIsIntentModalOpen={setIsIntentModalOpen}
        isInventoryAvailable={isInventoryAvaialable}
        brandName={brandName}
        modelName={modelName}
        variantName={variantName}
        cityName={cityName}
        isSellable={isSellable}
        pageType={pageType}
        carAvailability={carAvailability}
        selectedColor={selectedColor}
        savingBalance={savingBalance}
        environmentCompensation={environmentCompensation}
        fuelType={fuelType}
        selectedYear={selectedModelYear}
        onYearChange={handleModelYearChange}
        availableYears={availableYears}
      />
      {isSellable && variantDetails.is_car_finance_active && (
        <MMVCarLoan
          price={selectedDelivery}
          carFinanceData={carFinanceData}
          carFinanceBankData={carFinanceBankDetails}
          isLoggedIn={isLoggedIn}
          brandName={brandName}
          modelName={modelName}
          variantName={variantName}
          cityName={cityName}
          isSellable={isSellable}
          pageType={pageType}
          carAvailability={carAvailability}
        />
      )}
      {!isHeroCtaVisible && (
        <StickyCTA
          stockCount={inventoryAlertData?.alert_units}
          onTalkToUs={async () => {
            const results = await Promise.allSettled([
              leadCaptureHandler(true), // Pass true to indicate this is a callback request with high intent
              postRequestCallback({
                callback_message: `Request callback for ${brandName} ${modelName} ${variantName} in ${cityName}`,
              }),
            ]);
            const [leadResult, callbackResult] = results;
            if (
              leadResult.status === "fulfilled" &&
              callbackResult.status === "fulfilled"
            ) {
              handleRequestCallback();
            }
          }}
          onGetQuote={async () => {
            if (isLoggedIn) {
              await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
                pageType: pageType,
                additionalProps: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_get_detailed_quote",
                  car_availability: carAvailability,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  vehicle_variant: variantName,
                  city_name: cityName,
                  section: "Sticky CTA",
                },
              });
              leadCaptureHandler();
              handleGetQuote();
            } else {
              await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
                pageType: pageType,
                additionalProps: {
                  action_type: "tap_btn",
                  action_name: "tap_btn_login_to_save",
                  car_availability: carAvailability,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  vehicle_variant: variantName,
                  city_name: cityName,
                  section: "Sticky CTA",
                },
              });
              const mmvDetails = {
                make: brandName || "",
                model: modelName || "",
                variant: variantName || "",
                color: selectedColor?.brand_color || "",
              };
              if (typeof window !== "undefined") {
                sessionStorage.setItem("loginType", "Reveal price");
                localStorage.setItem(
                  "MMV",
                  JSON.stringify({
                    make: mmvDetails?.make || "",
                    model: mmvDetails?.model || "",
                    variant: mmvDetails?.variant || "",
                    color: mmvDetails?.color || "",
                  })
                );
              }
              const loginUrl = getLoginUrl(
                addCaptureIntentIfNeeded(
                  window.location.href,
                  mmvDetails,
                  "Reveal price",
                  pageType
                )
              );
              await handleLoginFlow(loginUrl, LoginSdkConfig.AD_PRICE);
            }
          }}
          isLoggedIn={isLoggedIn}
        />
      )}
      {isModalOpen && (
        <ModalManager
          isOpen={isModalOpen}
          onClose={() => {
            setTempSelectedDeliveryType(selectedDelivery?.deal_type?.toLowerCase() || "adsp");
            setIsModalOpen(false);
          }}
          title="Select your delivery option"
        >
          <div className={styles.deliveryOptions}>
            {/* Express Delivery Option */}
            <div
              className={
                `${styles.deliveryOptionCard} ` +
                (tempSelectedDeliveryType === "inventory"
                  ? styles.selected
                  : "")
              }
              onClick={() => {
                setTempSelectedDeliveryType("inventory");
              }}
            >
              <div className={styles.deliveryOptionDetails}>
                <span
                  className={`${styles.deliveryOptionTitle} ${styles.expressTitle}`}
                >
                  <Image
                    src={`${assetLoader()}/icons/thunder.svg`}
                    alt="Express Delivery"
                    width={16}
                    height={16}
                    loading="lazy"
                  />
                  <span>Express Delivery</span>
                </span>
                <div className={styles.deliveryOptionSubRow}>
                  <span className={styles.deliveryOptionSubLabel}>
                    Get your car in
                  </span>
                  <span className={styles.deliveryOptionDays}>
                    {getDaysUntilDateString(
                      deliveryOptions.inventory?.waiting_period
                    ) || "--"}{" "}
                    days
                  </span>
                </div>
              </div>
              <span className={styles.deliveryOptionPrice}>
                {deliveryOptions.inventory?.adsp_price
                  ? formatPrice(deliveryOptions.inventory.adsp_price)
                  : "--"}{" "}
              </span>
            </div>
            {/* Standard Delivery Option */}
            <div
              className={
                `${styles.deliveryOptionCard} ` +
                `${styles.standardCard} ` +
                (tempSelectedDeliveryType === "adsp"
                  ? styles.selected
                  : "")
              }
              onClick={() => {
                setTempSelectedDeliveryType("adsp");
              }}
            >
              <div className={styles.deliveryOptionDetails}>
                <span className={styles.deliveryOptionTitle}>
                  Standard Delivery
                </span>
                <div className={styles.deliveryOptionSubRow}>
                  <span className={styles.deliveryOptionSubLabel}>
                    Get your car in
                  </span>
                  <span className={styles.deliveryOptionDays}>
                    {getDaysUntilDateString(
                      deliveryOptions.adsp?.waiting_period
                    ) || "--"}{" "}
                    days
                  </span>
                </div>
              </div>
              <span className={styles.deliveryOptionPrice}>
                {deliveryOptions.adsp?.adsp_price
                  ? formatPrice(deliveryOptions.adsp.adsp_price)
                  : "--"}
              </span>
            </div>

            <Button
              className={styles.selectButton}
              onClick={handleSelectDelivery}
            >
              {"Select"}
            </Button>
          </div>
        </ModalManager>
      )}
      {isIntentModalOpen && (
        <IntentModal
          isOpen={isIntentModalOpen}
          onClose={() => setIsIntentModalOpen(false)}
          carDetails={carDetails}
          onRequestCallback={handleRequestCallback}
          onGetQuote={handleGetQuoteFromIntent}
          buyingIntent={buyingIntent}
          cityName={cityName}
          mmvDetails={{
            make: brandName,
            model: modelName,
            variant: variantName,
            color: selectedColor?.brand_color || "",
          }}
          highPriorityCapture={false}
          pageType={pageType}
          loginType={"Get quote"}
        />
      )}
      {isRequestCallBackModalOpen && (
        <RequestCallBackModal
          isOpen={isRequestCallBackModalOpen}
          onClose={() => setIsRequestCallBackModalOpen(false)}
        />
      )}

      {/* Mobile App Intent Modal */}
      {isMobileAppIntentModalOpen && (
        <GlobalIntentModal
          isOpen={isMobileAppIntentModalOpen}
          onClose={() => {
            setIsMobileAppIntentModalOpen(false);
            const currentParams = searchParams ? searchParams.toString() : "";
            const newSearchParams = new URLSearchParams(currentParams);
            newSearchParams.delete("captureIntent");
            const newUrl = newSearchParams.toString()
              ? `${pathname}?${newSearchParams.toString()}`
              : pathname;
            router.replace(newUrl || "");
          }}
          buyingIntent={buyingIntent}
          cityName={cityName}
          mmvDetails={{
            make: brandName,
            model: modelName,
            variant: variantName,
            color: selectedColor?.brand_color || "",
          }}
          pageType={pageType}
          loginType={"Get quote"}
        />
      )}
    </>
  );
}

export default MMVTopFold;